import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Sprout, Leaf, Flower2, Droplets, FlaskConical, Scissors, Package, Trash2, ShoppingCart, Droplet, Wind, Sun, Moon, Gauge, Beaker, Microscope, Zap, AlertTriangle, CheckCircle2, Eye, Waves, Dna } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";
import { usePhoneAudio as useSfx } from "@/components/cef/phoneAudio";

/* GROW overlay v2 — hydro simulation HUD. The UE5 camera sits INSIDE the tent
   staring at the plant through the transparent middle; everything lives on side
   rails: environment + light cycle left, plant vitals + root-zone chemistry right,
   stage timeline along the bottom. Feels like the tent's control computer, not a
   website card. */

const fmt$ = (n) => `$${(n ?? 0).toLocaleString()}`;
const STAGE_META = {
  seedling: { Icon: Sprout, label: "Seedling", color: "#86efac" },
  veg: { Icon: Leaf, label: "Vegetative", color: "#4ade80" },
  flower: { Icon: Flower2, label: "Flowering", color: "#c084fc" },
  ready: { Icon: Flower2, label: "Ready to chop", color: "#e879f9" },
  drying: { Icon: Wind, label: "Drying", color: "#a3a380" },
  dry: { Icon: Leaf, label: "Dry — cure or bag", color: "#d4d4a8" },
  curing: { Icon: Package, label: "Curing", color: "#f0abfc" },
  cured: { Icon: Package, label: "Cured — bag it", color: "#f0abfc" },
  dead: { Icon: Trash2, label: "Dead", color: "#7f1d1d" },
};
const TIMELINE = ["seedling", "veg", "flower", "ready", "drying", "curing", "cured"];
const QUALITY_COLOR = { schwag: "#a1a1aa", mid: "#86efac", premium: "#c084fc", exotic: "#f0abfc" };
const EC_TARGET = { seedling: [0.8, 1.2], veg: [1.2, 1.8], flower: [1.8, 2.4], ready: [0.4, 1.0] };

const Lcd = ({ label, value, unit, warn, good }) => (
  <div className="border border-white/10 bg-black/60 px-2.5 py-1.5">
    <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-500">{label}</p>
    <p className="font-mono text-base font-bold tracking-wide" style={{ color: warn ? "#f87171" : good ? "#4ade80" : "#e4e4e7", textShadow: `0 0 10px ${warn ? "#f8717166" : good ? "#4ade8066" : "#0000"}` }}>
      {value}<span className="text-[9px] text-zinc-500 ml-0.5">{unit}</span>
    </p>
  </div>
);

const Bar = ({ label, value, color, Icon, band }) => (
  <div>
    <div className="flex items-center gap-2 mb-0.5">
      <Icon className="w-3 h-3 shrink-0" style={{ color }} />
      <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <span className="ml-auto font-mono text-[9px] text-zinc-300">{value}</span>
    </div>
    <div className="relative h-1.5 bg-black/60 border border-white/10 overflow-hidden">
      {band && <div className="absolute top-0 bottom-0 bg-white/10" style={{ left: `${band[0]}%`, width: `${band[1] - band[0]}%` }} />}
      <div className="h-full transition-all duration-700" style={{ width: `${Math.min(100, value)}%`, backgroundColor: color, boxShadow: `0 0 8px ${color}` }} />
    </div>
  </div>
);

const ActionBtn = ({ testid, onClick, disabled, Icon, label, sub, accent = "#c084fc" }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className="border border-white/12 bg-black/50 hover:bg-white/5 disabled:opacity-30 px-2 py-2 flex flex-col items-center gap-0.5 transition-colors"
    style={{ borderLeft: `2px solid ${accent}` }}>
    <span className="flex items-center gap-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-zinc-100"><Icon className="w-3 h-3" style={{ color: accent }} />{label}</span>
    {sub && <span className="font-mono text-[7px] text-zinc-500 uppercase tracking-[0.1em]">{sub}</span>}
  </button>
);

export default function GrowCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [state, setState] = useState(null);
  const [growOps, setGrowOps] = useState(null); // v2: research, hybrids, market
  const [activeSlot, setActiveSlot] = useState(null);
  const [shopOpen, setShopOpen] = useState(false);
  const [geneticsOpen, setGeneticsOpen] = useState(false);
  const [breedA, setBreedA] = useState("");
  const [breedB, setBreedB] = useState("");
  const [busy, setBusy] = useState("");
  const sfx = useSfx();
  const pollRef = useRef(null);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const DEMO_STATE = {
    env: { temp_c: 23.4, humidity: 52, led_tier: 2, fan: true, filter: true, vpd: 1.35, co2_ppm: 1150, ppfd: 860, light_cycle: "12/12" },
    slots: [
      { slot: "tent-0", plant: { id: "demo-p1", slot: "tent-0", strain: "purple_haze", strain_name: "Violet Static", stage: "flower", growth: 71, health: 92, water: 64, nutrients: 48, ph: 5.9, ec: 2.1, trained: ["top", "lst"], flushing_mins: 0, trichomes: { cloudy: 62, amber: 4 }, needs_1212: false, lockout: false, burn: false, mold_risk: false, quality_preview: "exotic", est_yield_g: 44 } },
      { slot: "tent-1", plant: null },
      { slot: "cupboard-0", plant: { id: "demo-p2", slot: "cupboard-0", strain: "bornwood_bush", strain_name: "Bornwood Bush", stage: "veg", growth: 31, health: 77, water: 22, nutrients: 61, ph: 6.4, ec: 1.3, trained: [], flushing_mins: 0, trichomes: null, needs_1212: false, lockout: false, burn: false, mold_risk: false, quality_preview: "premium", est_yield_g: 38 } },
    ],
    lab: { equipment: { tent: true, led1: true, led2: true, fan: true, filter: true, cupboard: true, ph_kit: true, co2: true }, seeds: { og_kush: 1 }, ingredients: {} },
    suspicion: 12, rep: 8,
    shop: { equipment: { cupboard: { name: "DIY Grow Cupboard", cost: 450, desc: "1 slot, runs hot" }, tent: { name: "Hydro Grow Tent", cost: 1200, desc: "2 slots, stable humidity" }, led1: { name: "LED Panel", cost: 600, desc: "+20% growth" }, led2: { name: "LED Bar Array", cost: 1600, desc: "+45% growth", requires: "led1" }, fan: { name: "Ventilation Fan", cost: 350, desc: "-3°C" }, filter: { name: "Carbon Filter", cost: 700, desc: "kills the smell" }, ph_kit: { name: "pH Pen & Buffers", cost: 150, desc: "unlocks pH dosing" }, loupe: { name: "Trichome Loupe 60×", cost: 120, desc: "read resin heads" }, co2: { name: "CO₂ Tank", cost: 900, desc: "+15% growth" }, humidifier: { name: "Humidifier", cost: 400, desc: "+10% RH, steadies VPD" } }, seeds: { bornwood_bush: 40, purple_haze: 60, og_kush: 75, white_widow: 90 }, strains: { bornwood_bush: { name: "Bornwood Bush", grow_min: 80, yield_g: 46, price_g: 9, seed_cost: 40 }, purple_haze: { name: "Violet Static", grow_min: 95, yield_g: 38, price_g: 12, seed_cost: 60 }, og_kush: { name: "Old Harbour", grow_min: 110, yield_g: 42, price_g: 14, seed_cost: 75 }, white_widow: { name: "Whiteout", grow_min: 130, yield_g: 52, price_g: 17, seed_cost: 90 } } },
  };

  const load = useCallback(async () => {
    if (demo) {
      setState((s) => s || DEMO_STATE);
      setGrowOps((g) => g || { lab: { research_xp: 160, power_w: 420, production_skill: 3, rep: 3.8 }, hybrids: [], market: { supply: 20, price_mult: 1.33, mood: "steady" }, research_tiers: { 0: 0, 1: 150, 2: 400 } });
      return;
    }
    try {
      const [r, g] = await Promise.all([
        api.get(`/nc/servers/${serverId}/drugs/state`, { params: { token } }),
        api.get(`/nc/servers/${serverId}/grow/state`, { params: { token } }).catch(() => null),
      ]);
      setState(r.data);
      if (g) setGrowOps(g.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [demo, serverId, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 20000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const slots = state?.slots || [];
  const active = slots.find((s) => s.slot === activeSlot) || slots.find((s) => s.plant) || slots[0];
  const plant = active?.plant;
  const meta = plant ? STAGE_META[plant.stage] || STAGE_META.veg : null;
  const env = plant?.env || state?.env || {};
  const lightCycle = env.light_cycle || "18/6";

  const act = async (label, fn) => {
    if (busy) return;
    sfx.keyTick();
    setBusy(label);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
    if (!demo) load();
  };

  const buy = (item) => act(`buy-${item}`, async () => {
    if (demo) { toast.success("Installed (demo)"); return; }
    const r = await api.post(`/nc/servers/${serverId}/drugs/shop/buy`, { item }, { params: { token } });
    toast.success(r.data.message);
  });

  const plantSeed = (slot, strain) => act(`plant-${slot}`, async () => {
    if (demo) { toast.success(`Planted ${strain} (demo)`); return; }
    const r = await api.post(`/nc/servers/${serverId}/drugs/plant`, { slot, strain }, { params: { token } });
    toast.success(r.data.message);
  });

  const breed = () => act("breed", async () => {
    if (!breedA || !breedB || breedA === breedB) { toast.error("Pick two different parents"); return; }
    if (demo) { toast.success("Ember Haze stabilized — two seeds in the drawer (demo)"); setGeneticsOpen(true); return; }
    const r = await api.post(`/nc/servers/${serverId}/grow/breed`, { a: breedA, b: breedB }, { params: { token } });
    toast.success(r.data.message);
  });

  const plantAct = (plantId, action) => act(action, async () => {
    if (demo) { toast.success(`${action} (demo)`); return; }
    const r = await api.post(`/nc/servers/${serverId}/drugs/plants/${plantId}/${action}`, action === "care" ? {} : {}, { params: { token } });
    toast.success(r.data.message);
  });

  const care = (plantId, action) => act(action, async () => {
    if (demo) {
      // demo: move the gauges a little so the sim reads alive
      setState((s) => {
        if (!s) return s;
        const upd = (p) => {
          if (!p || p.id !== plantId) return p;
          const c = { ...p };
          if (action === "water") { c.water = 100; c.ec = Math.max(0.4, +(c.ec - 0.25).toFixed(2)); c.ph = +(c.ph + (6.0 - c.ph) * 0.15).toFixed(2); }
          if (action === "feed") { c.nutrients = 100; c.ec = Math.min(3.8, +(c.ec + 0.5).toFixed(2)); c.ph = +(c.ph - 0.08).toFixed(2); }
          if (action === "ph_up") c.ph = Math.min(8, +(c.ph + 0.3).toFixed(2));
          if (action === "ph_down") c.ph = Math.max(4, +(c.ph - 0.3).toFixed(2));
          if (["top", "lst", "defoliate"].includes(action) && !c.trained.includes(action)) c.trained = [...c.trained, action];
          if (action === "flush") { c.flushing_mins = 1; c.ec = 0.5; }
          return c;
        };
        return { ...s, slots: s.slots.map((sl) => ({ ...sl, plant: upd(sl.plant) })) };
      });
      toast.success(`${action} (demo)`);
      return;
    }
    const r = await api.post(`/nc/servers/${serverId}/drugs/plants/${plantId}/care`, { action }, { params: { token } });
    toast.success(r.data.message);
  });

  const setCycle = (cycle) => act(`cycle-${cycle}`, async () => {
    if (demo) { setState((s) => ({ ...s, env: { ...s.env, light_cycle: cycle } })); toast.success(`Timer flipped to ${cycle} (demo)`); return; }
    const r = await api.post(`/nc/servers/${serverId}/drugs/lightcycle`, { cycle }, { params: { token } });
    toast.success(r.data.message);
  });

  // one-line situational hint for the camera view
  const hint = !plant ? null
    : plant.lockout ? ["pH LOCKOUT — roots can't drink. Dose pH back into 5.6–6.4", "#f87171"]
    : plant.burn ? ["NUTRIENT BURN — EC too hot. Water it down or flush", "#f87171"]
    : plant.mold_risk ? ["MOLD RISK — humidity over 72% in flower. Drop it now", "#f87171"]
    : plant.needs_1212 ? ["PHOTOPERIOD HOLDING — flip the timer to 12/12 to trigger full flower", "#fbbf24"]
    : env.vpd && (env.vpd < 0.8 || env.vpd > 1.3) ? [`VPD OFF BAND (${env.vpd} kPa) — tune temp/humidity toward 0.8–1.3`, "#fbbf24"]
    : ["CONDITIONS OPTIMAL — she's stacking", "#4ade80"];

  const ecBand = plant ? (EC_TARGET[plant.stage] || EC_TARGET.flower) : [0, 0];

  return (
    <div data-testid="grow-page" className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif" }}>
      {demo && <PreviewRibbon what="grow tent" />}

      {/* top strip */}
      <div className="absolute top-3 inset-x-4 flex items-center gap-3 pointer-events-auto">
        <div className="border border-fuchsia-400/40 bg-black/70 backdrop-blur-md px-3 py-1.5 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-fuchsia-300">Live · tent cam 01 · hydro bay</p>
        </div>
        {/* slot pills */}
        <div className="flex gap-1">
          {slots.map((s) => (
            <button key={s.slot} data-testid={`grow-slot-${s.slot}`} onClick={() => { setActiveSlot(s.slot); sfx.tick(); }}
              className={`px-3 py-1.5 font-mono text-[8px] uppercase tracking-[0.15em] border backdrop-blur-md transition-colors ${active?.slot === s.slot ? "border-fuchsia-400/60 text-fuchsia-200 bg-fuchsia-500/15" : "border-white/15 text-zinc-400 bg-black/60 hover:text-zinc-200"}`}>
              {s.slot.replace("-", " ")}{s.plant ? " ●" : " ○"}
            </button>
          ))}
        </div>
        <div className="ml-auto border border-white/15 bg-black/70 backdrop-blur-md px-3 py-1.5">
          <p className="font-mono text-[9px] text-zinc-400 uppercase tracking-[0.2em]">Heat {state?.suspicion ?? 0} · Rep {state?.rep ?? 0}</p>
        </div>
      </div>

      {/* camera viewfinder frame — the 3D plant shows through the middle */}
      <div className="absolute left-[300px] right-[336px] top-16 bottom-16 pointer-events-none">
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-fuchsia-400/60" />
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-fuchsia-400/60" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-fuchsia-400/60" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-fuchsia-400/60" />
        {plant && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 border border-white/15 bg-black/60 backdrop-blur-md px-4 py-1.5 text-center">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-100">{plant.strain_name}</p>
            <p className="font-mono text-[8px] uppercase tracking-[0.25em]" style={{ color: meta.color }}>{meta.label} · {plant.growth}% · {plant.est_yield_g}g est</p>
          </div>
        )}
        {hint && (
          <div data-testid="grow-hint" className="absolute bottom-2 left-1/2 -translate-x-1/2 border bg-black/70 backdrop-blur-md px-4 py-2 flex items-center gap-2 max-w-md"
            style={{ borderColor: `${hint[1]}55` }}>
            {hint[1] === "#4ade80" ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: hint[1] }} /> : <AlertTriangle className="w-3.5 h-3.5 shrink-0" style={{ color: hint[1] }} />}
            <p className="font-mono text-[8px] uppercase tracking-[0.15em]" style={{ color: hint[1] }}>{hint[0]}</p>
          </div>
        )}
      </div>

      {/* LEFT RAIL — environment + light cycle + equipment/shop */}
      <div data-testid="grow-rail-env" className="absolute left-4 top-16 bottom-16 w-[280px] pointer-events-auto flex flex-col gap-2 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
        <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-3">
          <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-fuchsia-300 mb-2 flex items-center gap-1.5"><Gauge className="w-3 h-3" /> Tent environment</p>
          <div className="grid grid-cols-2 gap-1.5">
            <Lcd label="Temp" value={env.temp_c ?? "—"} unit="°C" warn={env.temp_c < 18 || env.temp_c > 28} good={env.temp_c >= 20 && env.temp_c <= 26} />
            <Lcd label="Humidity" value={env.humidity ?? "—"} unit="%" warn={env.humidity < 35 || env.humidity > 70} good={env.humidity >= 45 && env.humidity <= 65} />
            <Lcd label="VPD" value={env.vpd ?? "—"} unit="kPa" warn={env.vpd < 0.5 || env.vpd > 1.6} good={env.vpd >= 0.8 && env.vpd <= 1.3} />
            <Lcd label="CO₂" value={env.co2_ppm ?? "—"} unit="ppm" good={env.co2_ppm > 800} />
            <Lcd label="PPFD" value={env.ppfd ?? 0} unit="µmol" good={env.ppfd >= 400} warn={!env.ppfd} />
            <Lcd label="Light" value={["NONE", "LED", "LED×3"][env.led_tier || 0]} unit="" warn={!env.led_tier} />
          </div>
        </div>

        <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-3">
          <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-fuchsia-300 mb-2 flex items-center gap-1.5"><Sun className="w-3 h-3" /> Photoperiod timer</p>
          <div className="grid grid-cols-2 gap-1.5">
            {[["18/6", "Veg", Sun], ["12/12", "Flower", Moon]].map(([cyc, lbl, Icon]) => (
              <button key={cyc} data-testid={`grow-cycle-${cyc.replace("/", "")}`} onClick={() => setCycle(cyc)} disabled={!!busy}
                className={`py-2.5 border font-mono text-[10px] font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 ${lightCycle === cyc ? "border-fuchsia-400/70 text-fuchsia-200 bg-fuchsia-500/15" : "border-white/15 text-zinc-400 hover:text-zinc-200"}`}>
                <Icon className="w-3.5 h-3.5" /> {cyc} {lbl}
              </button>
            ))}
          </div>
          <p className="font-mono text-[7px] text-zinc-600 mt-1.5 leading-relaxed uppercase tracking-[0.1em]">Photoperiod strains won't stack buds until you flip to 12/12</p>
        </div>

        <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-3 flex-1">
          <button data-testid="grow-shop-toggle" onClick={() => setShopOpen((o) => !o)}
            className="w-full flex items-center justify-between font-mono text-[8px] uppercase tracking-[0.3em] text-fuchsia-300 mb-2 hover:text-fuchsia-100 transition-colors">
            <span className="flex items-center gap-1.5"><ShoppingCart className="w-3 h-3" /> Hydro supply</span>
            <span>{shopOpen ? "−" : "+"}</span>
          </button>
          {!shopOpen ? (
            <div className="flex flex-col gap-1">
              {Object.entries(state?.lab?.equipment || {}).filter(([, v]) => v).map(([k]) => (
                <p key={k} className="font-mono text-[8px] uppercase tracking-[0.15em] text-zinc-400 flex items-center gap-1.5">
                  <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" /> {state.shop.equipment[k]?.name || k}
                </p>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {Object.entries(state?.shop?.equipment || {}).map(([id, eq]) => (
                <div key={id} data-testid={`grow-shop-${id}`} className="flex items-center gap-2 border border-white/10 bg-black/40 px-2 py-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[9px] text-zinc-100">{eq.name}</p>
                    <p className="font-mono text-[7px] text-zinc-500">{eq.desc}</p>
                  </div>
                  <button data-testid={`grow-buy-${id}`} onClick={() => buy(id)} disabled={busy === `buy-${id}` || state.lab.equipment[id]}
                    className="shrink-0 px-2 py-1 font-mono text-[8px] font-bold uppercase border border-fuchsia-400/50 text-fuchsia-300 hover:bg-fuchsia-500/10 disabled:opacity-30 transition-colors">
                    {state.lab.equipment[id] ? "Owned" : fmt$(eq.cost)}
                  </button>
                </div>
              ))}
              <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Seed packs</p>
              {Object.entries(state?.shop?.strains || {}).map(([id, st]) => (
                <div key={id} data-testid={`grow-seed-${id}`} className="flex items-center gap-2 border border-white/10 bg-black/40 px-2 py-1.5">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[9px] text-zinc-100">{st.name}</p>
                    <p className="font-mono text-[7px] text-zinc-500">{st.grow_min}min · ~{st.yield_g}g · {fmt$(st.price_g)}/g · have {state.lab.seeds?.[id] || 0}</p>
                  </div>
                  <button data-testid={`grow-buy-seed-${id}`} onClick={() => buy(`seed_${id}`)} disabled={busy === `buy-seed_${id}`}
                    className="shrink-0 px-2 py-1 font-mono text-[8px] font-bold uppercase border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-30 transition-colors">
                    {fmt$(st.seed_cost)}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* GENETICS LAB — research tiers, cross-breeding, server-unique hybrids */}
          <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-3">
            <button data-testid="genetics-toggle" onClick={() => { setGeneticsOpen((o) => !o); sfx.tick(); }}
              className="w-full flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.25em] text-lime-300">
              <span className="flex items-center gap-2"><Dna className="w-3.5 h-3.5" /> Genetics lab</span>
              <span className="text-zinc-500">{geneticsOpen ? "▲" : "▼"}</span>
            </button>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex-1">
                <div className="flex justify-between font-mono text-[7px] text-zinc-500 uppercase tracking-[0.15em]"><span>Research</span><span>{growOps?.lab?.research_xp ?? 0} XP</span></div>
                <div className="h-1.5 bg-black/60 border border-white/10 mt-1">
                  <div data-testid="genetics-xp-bar" className="h-full bg-lime-400 transition-all" style={{ width: `${Math.min(100, ((growOps?.lab?.research_xp ?? 0) / 400) * 100)}%` }} />
                </div>
              </div>
              <span className="font-mono text-[8px] px-1.5 py-0.5 border border-lime-400/40 text-lime-300 uppercase">Tier {(growOps?.lab?.research_xp ?? 0) >= 400 ? 2 : (growOps?.lab?.research_xp ?? 0) >= 150 ? 1 : 0}</span>
            </div>
            {geneticsOpen && growOps && (
              <div className="mt-2 flex flex-col gap-2" data-testid="genetics-panel">
                <p className="font-mono text-[7px] text-zinc-500 leading-relaxed">Cross two strains you own seeds for — the hybrid's genetics are unique to this server. Tier 1 unlocks at 150 XP, tier 2 at 400. Harvests, breeding and lab work all earn XP.</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {[["genetics-parent-a", breedA, setBreedA], ["genetics-parent-b", breedB, setBreedB]].map(([tid, val, setter]) => (
                    <select key={tid} data-testid={tid} value={val} onChange={(e) => setter(e.target.value)}
                      className="bg-black/60 border border-white/10 px-2 py-1.5 font-mono text-[9px] text-zinc-200 focus:outline-none">
                      <option value="">parent…</option>
                      {Object.entries(state?.shop?.strains || {}).map(([id, st]) => (
                        <option key={id} value={id}>{st.name} ×{state.lab.seeds?.[id] || 0}</option>
                      ))}
                      {(growOps.hybrids || []).map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                    </select>
                  ))}
                </div>
                <button data-testid="genetics-cross" onClick={breed} disabled={!!busy || !breedA || !breedB || breedA === breedB}
                  className="py-2.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-30 transition-opacity flex items-center justify-center gap-1.5" style={{ backgroundColor: "#a3e635" }}>
                  <Dna className="w-3.5 h-3.5" /> Cross-breed (+40 XP)
                </button>
                {(growOps.hybrids || []).length > 0 && (
                  <div className="flex flex-col gap-1">
                    <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-500">Your genetics</p>
                    {growOps.hybrids.map((h) => (
                      <p key={h.id} data-testid={`hybrid-${h.id}`} className="font-mono text-[9px] text-lime-200 border border-lime-500/20 bg-lime-950/20 px-2 py-1.5">
                        {h.name} <span className="text-zinc-500">· pot {h.potency} · smell {h.smell_m}m · {h.yield_g}g</span>
                      </p>
                    ))}
                  </div>
                )}
                {/* street market pulse */}
                {growOps.market && (
                  <div className="border border-white/10 bg-black/40 px-2.5 py-2 flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${growOps.market.mood === "tight" ? "bg-emerald-400" : growOps.market.mood === "flooded" ? "bg-red-400" : "bg-amber-400"}`} />
                    <p className="font-mono text-[8px] text-zinc-400 uppercase tracking-[0.15em]">street {growOps.market.mood} · prices at <span className="text-zinc-100 font-bold">{Math.round(growOps.market.price_mult * 100)}%</span></p>
                  </div>
                )}
                {/* production shortcuts */}
                <div className="grid grid-cols-2 gap-1.5">
                  <a data-testid="open-rolling" href={`/cef/roll/${serverId}${demo ? "" : `?token=${token}`}`}
                    className="py-2 font-mono text-[8px] font-bold uppercase tracking-[0.15em] text-center border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors">Rolling tray →</a>
                  <a data-testid="open-concentrates" href={`/cef/lab/${serverId}${demo ? "" : `?token=${token}`}`}
                    className="py-2 font-mono text-[8px] font-bold uppercase tracking-[0.15em] text-center border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 transition-colors">Concentrate lab →</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT RAIL — plant vitals + root zone + training + actions */}
      <div data-testid="grow-rail-plant" className="absolute right-4 top-16 bottom-16 w-[320px] pointer-events-auto flex flex-col gap-2 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
        {!state ? (
          <div className="border border-white/12 bg-[#0d0614ee] p-8 text-center font-mono text-[10px] text-zinc-500 animate-pulse">Opening the tent flap…</div>
        ) : plant ? (
          <>
            <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-3">
              <div className="flex items-center gap-2 mb-2">
                <meta.Icon className="w-5 h-5" style={{ color: meta.color, filter: `drop-shadow(0 0 10px ${meta.color})` }} />
                <div className="flex-1">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-100">{plant.strain_name}</p>
                  <p className="font-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: QUALITY_COLOR[plant.quality_preview] }}>looking {plant.quality_preview}</p>
                </div>
                <span className="font-mono text-[8px] uppercase border px-1.5 py-0.5" style={{ color: meta.color, borderColor: `${meta.color}55` }}>{meta.label}</span>
              </div>
              <Bar label="Growth" value={`${plant.growth}%`} color="#c084fc" Icon={Leaf} />
              <div className="mt-1.5"><Bar label="Health" value={`${Math.round(plant.health)}%`} color={plant.health > 40 ? "#4ade80" : "#f87171"} Icon={Zap} /></div>
            </div>

            <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-3">
              <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-fuchsia-300 mb-2 flex items-center gap-1.5"><Beaker className="w-3 h-3" /> Root zone</p>
              <div className="flex flex-col gap-1.5">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <FlaskConical className="w-3 h-3 text-sky-300" />
                    <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500">pH</span>
                    <span className="ml-auto font-mono text-[9px]" style={{ color: plant.ph >= 5.6 && plant.ph <= 6.4 ? "#4ade80" : "#f87171" }}>{plant.ph}</span>
                  </div>
                  <div className="relative h-1.5 bg-black/60 border border-white/10">
                    <div className="absolute top-0 bottom-0 bg-emerald-400/15" style={{ left: "40%", width: "20%" }} />
                    <div className="absolute top-[-2px] w-[3px] h-[10px] transition-all duration-700" style={{ left: `${Math.min(98, Math.max(0, ((plant.ph - 4) / 4) * 100))}%`, backgroundColor: plant.ph >= 5.6 && plant.ph <= 6.4 ? "#4ade80" : "#f87171", boxShadow: "0 0 8px currentColor" }} />
                  </div>
                  <p className="font-mono text-[7px] text-zinc-600 mt-0.5">band 5.6–6.4 · lockout outside 5.0–7.0</p>
                </div>
                <Bar label={`EC / PPM (target ${ecBand[0]}–${ecBand[1]})`} value={plant.ec} color={plant.ec > 3.0 ? "#f87171" : plant.ec >= ecBand[0] && plant.ec <= ecBand[1] ? "#4ade80" : "#fbbf24"} Icon={Waves} />
                <Bar label="H₂O" value={`${Math.round(plant.water)}%`} color="#38bdf8" Icon={Droplet} />
                <Bar label="Nutrients" value={`${Math.round(plant.nutrients)}%`} color="#a3e635" Icon={FlaskConical} />
              </div>
            </div>

            {plant.trichomes && (
              <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-3" data-testid="grow-trichomes">
                <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-fuchsia-300 mb-2 flex items-center gap-1.5"><Microscope className="w-3 h-3" /> Trichomes (loupe)</p>
                <div className="h-2 bg-black/60 border border-white/10 overflow-hidden flex">
                  <div className="h-full" style={{ width: `${plant.trichomes.cloudy}%`, backgroundColor: "#e2e8f0", boxShadow: "0 0 8px #e2e8f088" }} />
                  <div className="h-full" style={{ width: `${plant.trichomes.amber}%`, backgroundColor: "#f59e0b", boxShadow: "0 0 8px #f59e0b88" }} />
                </div>
                <div className="flex justify-between font-mono text-[7px] text-zinc-500 mt-1 uppercase tracking-[0.1em]">
                  <span>cloudy {Math.round(plant.trichomes.cloudy)}%</span><span>amber {Math.round(plant.trichomes.amber)}%</span>
                </div>
                <p className="font-mono text-[7px] text-zinc-500 mt-1">Peak: 70–90% cloudy, amber under 25%. More amber = sleepier stone, lower price.</p>
              </div>
            )}
            {!plant.trichomes && plant.stage === "flower" && (
              <p className="font-mono text-[7px] text-zinc-500 border border-dashed border-white/10 px-2 py-1.5">Buy the Trichome Loupe to read resin heads and time the chop</p>
            )}

            <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-3">
              <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-fuchsia-300 mb-2">Training · {plant.trained.length ? plant.trained.join(" + ").toUpperCase() : "none"}</p>
              <div className="grid grid-cols-3 gap-1.5">
                <ActionBtn testid="grow-top" onClick={() => care(plant.id, "top")} disabled={!!busy || plant.trained.includes("top") || plant.stage !== "veg"} Icon={Scissors} label="Top" sub="veg · +qual" />
                <ActionBtn testid="grow-lst" onClick={() => care(plant.id, "lst")} disabled={!!busy || plant.trained.includes("lst")} Icon={Waves} label="LST" sub="bend · +qual" />
                <ActionBtn testid="grow-defoliate" onClick={() => care(plant.id, "defoliate")} disabled={!!busy || plant.trained.includes("defoliate") || plant.stage !== "flower"} Icon={Eye} label="Defol" sub="flower · +qual" />
              </div>
            </div>

            <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-3">
              <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-fuchsia-300 mb-2">Reservoir & care</p>
              {!["ready", "drying", "dry", "curing", "cured", "dead"].includes(plant.stage) ? (
                <div className="grid grid-cols-3 gap-1.5">
                  <ActionBtn testid="grow-water" onClick={() => care(plant.id, "water")} disabled={!!busy} Icon={Droplets} label="Water" sub="$8 · dilutes EC" accent="#38bdf8" />
                  <ActionBtn testid="grow-feed" onClick={() => care(plant.id, "feed")} disabled={!!busy} Icon={FlaskConical} label="Feed" sub="$15 · EC up" accent="#a3e635" />
                  <ActionBtn testid="grow-flush" onClick={() => care(plant.id, "flush")} disabled={!!busy || plant.stage !== "flower"} Icon={Wind} label="Flush" sub="late flower" accent="#67e8f9" />
                  <ActionBtn testid="grow-ph-up" onClick={() => care(plant.id, "ph_up")} disabled={!!busy} Icon={Beaker} label="pH +" sub="$5" accent="#fbbf24" />
                  <ActionBtn testid="grow-ph-down" onClick={() => care(plant.id, "ph_down")} disabled={!!busy} Icon={Beaker} label="pH −" sub="$5" accent="#fbbf24" />
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {plant.stage === "ready" && (
                    <button data-testid="grow-harvest" onClick={() => plantAct(plant.id, "harvest")} disabled={!!busy}
                      className="py-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-black flex items-center justify-center gap-2 disabled:opacity-40"
                      style={{ backgroundColor: "#e879f9", boxShadow: "0 0 20px #e879f944" }}>
                      <Scissors className="w-3.5 h-3.5" /> Chop her down
                    </button>
                  )}
                  {plant.stage === "dry" && (
                    <div className="grid grid-cols-2 gap-1.5">
                      <button data-testid="grow-cure" onClick={() => plantAct(plant.id, "cure")} disabled={!!busy}
                        className="py-2.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] border border-fuchsia-400/50 text-fuchsia-300 hover:bg-fuchsia-500/10 disabled:opacity-40">Cure (better price)</button>
                      <button data-testid="grow-collect" onClick={() => plantAct(plant.id, "collect")} disabled={!!busy}
                        className="py-2.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-black disabled:opacity-40" style={{ backgroundColor: "#4ade80" }}>Bag it now</button>
                    </div>
                  )}
                  {plant.stage === "cured" && (
                    <button data-testid="grow-collect-cured" onClick={() => plantAct(plant.id, "collect")} disabled={!!busy}
                      className="py-3 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-black disabled:opacity-40" style={{ backgroundColor: "#4ade80" }}>Bag the cure</button>
                  )}
                  {["drying", "curing"].includes(plant.stage) && (
                    <p className="text-center font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 animate-pulse py-2">{plant.stage === "drying" ? "Hanging to dry…" : "Sweating in the jars…"}</p>
                  )}
                  {plant.stage === "dead" && (
                    <p className="text-center font-mono text-[8px] uppercase tracking-[0.25em] text-red-400 py-2">Dead of neglect — clear the pot and replant</p>
                  )}
                </div>
              )}
              {plant.flushing_mins > 0 && <p className="font-mono text-[7px] text-cyan-300 mt-1.5 uppercase tracking-[0.1em]">Flushing {Math.round(plant.flushing_mins)}min — chop after 30 for the clean finish bonus</p>}
            </div>
          </>
        ) : (
          <div className="border border-white/12 bg-[#0d0614ee] backdrop-blur-md p-4">
            <Sprout className="w-8 h-8 mx-auto text-zinc-700 mb-2" />
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 text-center mb-3">Empty pot — pick genetics</p>
            {active ? (
              <div className="flex flex-col gap-1.5">
                {Object.entries(state.shop.strains).map(([id, st]) => (
                  <button key={id} data-testid={`grow-plant-${id}`} onClick={() => plantSeed(active.slot, id)} disabled={!!busy || !(state.lab.seeds?.[id] > 0)}
                    className="border border-white/10 hover:border-fuchsia-400/50 bg-black/40 px-3 py-2 text-left disabled:opacity-30 transition-colors">
                    <p className="font-mono text-[10px] text-zinc-100">{st.name}</p>
                    <p className="font-mono text-[7px] text-zinc-500">{st.grow_min}min · ~{st.yield_g}g · seeds: {state.lab.seeds?.[id] || 0}</p>
                  </button>
                ))}
              </div>
            ) : (
              <p className="font-mono text-[9px] text-zinc-500 text-center">No grow equipment yet — hydro supply, left rail</p>
            )}
          </div>
        )}
      </div>

      {/* bottom strip — lifecycle timeline */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-auto border border-white/12 bg-black/70 backdrop-blur-md px-4 py-2 flex items-center gap-0">
        {TIMELINE.map((st, i) => {
          const activeStage = plant?.stage;
          const idx = activeStage ? TIMELINE.indexOf(activeStage) : -1;
          const done = idx > i, current = idx === i;
          return (
            <div key={st} className="flex items-center">
              <span className={`font-mono text-[7px] uppercase tracking-[0.15em] px-1.5 ${current ? "text-fuchsia-300 font-bold" : done ? "text-emerald-400" : "text-zinc-600"}`}>
                {st === "ready" ? "chop" : st}
              </span>
              {i < TIMELINE.length - 1 && <span className={`w-4 h-px ${done ? "bg-emerald-400" : "bg-zinc-700"}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
